import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { omit } from 'lodash';
import { ApiService } from 'src/app/core/services';
import {
  CreateSubscriptionParams,
  PlanSubscription,
  DeleteSubscriptionAttachmentParams,
  DeleteSubscriptionAttachmentResponse,
  GetSubscriptionAttachmentUrlsParams,
  GetSubscriptionAttachmentUrlsResponse,
  GetSubscriptionsParams,
  GetSubscriptionsResponse,
  UpdateSubscriptionParams,
  UpdateSubscriptionRenewalParams,
  GetSubscriptionHistoryParams,
  GetSubscriptionHistoryResponse,
} from 'src/app/core';

@Injectable({
  providedIn: 'root',
})
export class SubscriptionsService {
  constructor(private readonly apiService: ApiService) {}

  /**
   * Get Subscriptions
   * permissions: {mode: "+r", res: "subscription.*"}
   */
  getSubscriptions(
    getSubscriptionParams: GetSubscriptionsParams
  ): Observable<GetSubscriptionsResponse> {
    const { lineId, phoneNumber, subscriptionId, groupId } =
      getSubscriptionParams;
    const params: HttpParams = this.apiService.queryParams(
      omit(getSubscriptionParams, ['lineId', 'phoneNumber', 'subscriptionId'])
    );

    if (lineId) {
      return this.apiService.get(`/v2/lines/${lineId}/subscriptions`, params);
    } else if (phoneNumber) {
      return this.apiService.get(
        `/v2/phone-numbers/${phoneNumber}/subscriptions`,
        params
      );
    } else if (groupId) {
      return this.apiService.get(`/v2/groups/${groupId}/subscriptions`, params);
    }

    return this.apiService.get(`/v2/subscriptions/${subscriptionId}`, params);
  }

  /**
   * Create PlanSubscription
   * permissions: {mode: "+cru", res: "subscription.*"}
   */
  createSubscription(
    createSubscriptionParams: CreateSubscriptionParams
  ): Observable<PlanSubscription> {
    return this.apiService.post('/v2/subscriptions', createSubscriptionParams);
  }

  /**
   * Update PlanSubscription
   * permissions: {mode: "+ur", res: "subscription.*"}
   */
  updateSubscription(
    updateSubscriptionParams: UpdateSubscriptionParams
  ): Observable<PlanSubscription> {
    return this.apiService.put(
      `/v2/subscriptions/${updateSubscriptionParams.subscriptionId}`,
      updateSubscriptionParams
    );
  }

  /**
   * Get Subscription Attachment Urls
   * permissions: {mode: "+r", res: "subscription.*"}
   */
  getSubscriptionAttachmentUrls(
    getSubscriptionAttachmentUrlsParams: GetSubscriptionAttachmentUrlsParams
  ): Observable<GetSubscriptionAttachmentUrlsResponse> {
    const params: HttpParams = this.apiService.queryParams(
      getSubscriptionAttachmentUrlsParams
    );

    return this.apiService.get(
      `/v2/subscriptions/${getSubscriptionAttachmentUrlsParams.subscriptionId}/attachments`,
      params
    );
  }

  uploadSubscriptionAttachment(url: string): Observable<void> {
    return this.apiService.putExternal(url);
  }

  /**
   * Delete Subscription Attachments
   * permissions: {mode: "+rud", res: "subscription.*"}
   */
  deleteSubscriptionAttachments(
    deleteSubscriptionAttachmentParams: DeleteSubscriptionAttachmentParams
  ): Observable<DeleteSubscriptionAttachmentResponse> {
    let params = new HttpParams();

    Object.keys(deleteSubscriptionAttachmentParams).forEach((param) => {
      params = params.append(param, deleteSubscriptionAttachmentParams[param]);
    });

    return this.apiService.delete(
      `/v2/subscriptions/${deleteSubscriptionAttachmentParams.subscriptionId}/attachments`,
      params
    );
  }

  /**
   * Update Subscription Renewal
   * permissions: {mode: "+ur", res: "subscription.*"}
   */
  updateSubscriptionRenewal(
    updateSubscriptionRenewalParams: UpdateSubscriptionRenewalParams
  ): Observable<PlanSubscription> {
    return this.apiService.put(
      `/v2/subscriptions/${updateSubscriptionRenewalParams.subscriptionId}/renewal`,
      updateSubscriptionRenewalParams
    );
  }

  /**
   * Get Subscription History
   * permissions: {mode: "+r", res: "subscription.*"}
   */
  getSubscriptionHistory(
    getSubscriptionHistoryParams: GetSubscriptionHistoryParams
  ): Observable<GetSubscriptionHistoryResponse> {
    const params: HttpParams = this.apiService.queryParams(
      getSubscriptionHistoryParams
    );

    return this.apiService.get(
      `/v2/subscriptions/${getSubscriptionHistoryParams.subscriptionId}/history`,
      params
    );
  }
}
